import { Box, Typography } from "@mui/material";

// Formats text with paragraphs, markdown headers, and optional highlights.
// Props:
//   - text: raw text to display
//   - highlightSpans: optional character ranges to highlight in the original text

interface ReferenceFormattedTextProps {
  text: string;
  highlightSpans?: { start: number; end: number }[];
}

export const ReferenceFormattedText = ({
  text,
  highlightSpans = [],
}: ReferenceFormattedTextProps) => {
  const originalText = text;

  // Step 1: Normalize line breaks (convert \r\n to \n, merge single newlines into spaces)
  const normalized = text
    .replace(/\r\n/g, "\n")
    .replace(/([^\n])\n([^\n])/g, "$1 $2");

  // Step 2: Split into paragraphs (double newlines act as paragraph breaks)
  const paragraphs = normalized.split(/\n{2,}/);

  // Step 3: Build position mapping from original text to normalized text
  // This enables accurate highlight positioning across text transformations
  const origToNorm: Map<number, number> = new Map();
  let normIndex = 0;
  let origIndex = 0;

  while (origIndex < originalText.length) {
    origToNorm.set(origIndex, normIndex);

    const char = originalText[origIndex];
    const nextChar =
      origIndex < originalText.length - 1 ? originalText[origIndex + 1] : "";

    // Skip \r in \r\n pairs
    if (char === "\r" && nextChar === "\n") {
      origIndex++;
      continue;
    }

    normIndex++;
    origIndex++;
  }

  // Build mapping from normalized text to formatted text (after markdown removal)
  const normToFormatted: Map<number, { paraIndex: number; charIndex: number }> =
    new Map();

  const formattedParagraphs: Array<{
    displayText: string;
    originalTrimmed: string;
    paraIndex: number;
  }> = [];

  paragraphs.forEach((para, paraIndex) => {
    const trimmed = para.trim();
    if (!trimmed) return;

    // Remove markdown prefixes to get display text
    let displayText = trimmed;
    if (trimmed.startsWith("### ")) {
      displayText = trimmed.substring(4);
    } else if (trimmed.startsWith("## ")) {
      displayText = trimmed.substring(3);
    } else if (trimmed.startsWith("# ")) {
      displayText = trimmed.substring(2);
    }

    formattedParagraphs.push({
      displayText,
      originalTrimmed: trimmed,
      paraIndex,
    });
  });

  // Map each character position through the transformations
  let searchPos = 0;
  formattedParagraphs.forEach(({ displayText, originalTrimmed, paraIndex }) => {
    const paraStartInNorm = normalized.indexOf(originalTrimmed, searchPos);
    if (paraStartInNorm === -1) return;

    const markdownPrefixLen = originalTrimmed.length - displayText.length;

    for (let i = 0; i < originalTrimmed.length; i++) {
      const normPos = paraStartInNorm + i;

      if (i < markdownPrefixLen) {
        // Markdown prefix chars map to position 0 in display text
        normToFormatted.set(normPos, { paraIndex, charIndex: 0 });
      } else {
        // Regular chars map to their position after prefix removal
        normToFormatted.set(normPos, {
          paraIndex,
          charIndex: i - markdownPrefixLen,
        });
      }
    }

    searchPos = paraStartInNorm + originalTrimmed.length;
  });

  // Step 4: Render paragraphs with appropriate styling and highlights
  const processedNodes: React.ReactNode[] = [];

  paragraphs.forEach((para, paraIndex) => {
    const trimmed = para.trim();
    if (!trimmed) return;

    let hasListNumberWithSummary = false;
    let listNumber = "";

    // Detect markdown prefix
    let markdownPrefix = "";
    if (trimmed.startsWith("### ")) markdownPrefix = "### ";
    else if (trimmed.startsWith("## ")) markdownPrefix = "## ";
    else if (trimmed.startsWith("# ")) markdownPrefix = "# ";

    let displayText = markdownPrefix
      ? trimmed.replace(markdownPrefix, "")
      : trimmed;

    // Check if content is truncated/summarized
    const hasSummaryMarker = trimmed.includes("[...]");

    // Determine styling based on markdown level and summarization
    let sx;
    if (markdownPrefix === "### ") {
      sx = hasSummaryMarker
        ? { fontSize: "0.85rem", lineHeight: 1.4, mb: 0.2 }
        : { fontWeight: 550, fontSize: "0.9rem", mt: 0.7, mb: 0.3 };
    } else if (markdownPrefix === "## ") {
      sx = hasSummaryMarker
        ? { fontSize: "0.85rem", lineHeight: 1.4, mb: 0.2 }
        : { fontWeight: 600, fontSize: "0.98rem", mt: 0.6, mb: 0.25 };
    } else if (markdownPrefix === "# ") {
      sx = hasSummaryMarker
        ? { fontSize: "0.85rem", lineHeight: 1.4, mb: 0.2 }
        : { fontWeight: 700, fontSize: "1.1rem", mt: 0.8, mb: 0.4 };
    } else {
      // Default styling for list items and regular paragraphs
      sx = { fontSize: "0.85rem", lineHeight: 1.4, mb: 0.2 };
    }

    // Extract and remove list numbers from summarized headers (e.g., "### 1. [...]")
    if (
      markdownPrefix &&
      hasSummaryMarker &&
      /^(\d+\.)\s*\[/.test(displayText)
    ) {
      hasListNumberWithSummary = true;
      listNumber = displayText.match(/^(\d+\.)/)?.[1] || "";
      displayText = displayText.replace(/^(\d+\.)\s*/, "");
    }

    // Calculate which highlights apply to this paragraph
    const paragraphHighlights: { start: number; end: number }[] = [];

    highlightSpans.forEach((span) => {
      const normStart = origToNorm.get(span.start);
      const normEnd = origToNorm.get(Math.max(0, span.end - 1));

      if (normStart === undefined || normEnd === undefined) return;

      const formattedStart = normToFormatted.get(normStart);
      const formattedEnd = normToFormatted.get(normEnd);

      if (!formattedStart || !formattedEnd) return;

      const startsBeforeOrIn = formattedStart.paraIndex <= paraIndex;
      const endsInOrAfter = formattedEnd.paraIndex >= paraIndex;

      if (startsBeforeOrIn && endsInOrAfter) {
        // Adjust for removed list number
        const listNumberOffset = hasListNumberWithSummary
          ? listNumber.length + 1
          : 0;

        let localStart: number;
        let localEnd: number;

        if (formattedStart.paraIndex === paraIndex) {
          localStart = formattedStart.charIndex - listNumberOffset;
        } else {
          localStart = 0;
        }

        if (formattedEnd.paraIndex === paraIndex) {
          localEnd = formattedEnd.charIndex + 1 - listNumberOffset;
        } else {
          localEnd = displayText.length;
        }

        localStart = Math.max(0, localStart);
        localEnd = Math.min(displayText.length, localEnd);

        if (localEnd > localStart) {
          paragraphHighlights.push({
            start: localStart,
            end: localEnd,
          });
        }
      }
    });

    // Render paragraph with highlights if any
    if (paragraphHighlights.length > 0) {
      const parts: React.ReactNode[] = [];

      // Add bold list number if present
      if (hasListNumberWithSummary && listNumber) {
        parts.push(<strong key={`${paraIndex}-num`}>{listNumber} </strong>);
      }

      let lastIndex = 0;

      paragraphHighlights
        .sort((a, b) => a.start - b.start)
        .forEach((span, spanIdx) => {
          // Add unhighlighted text before this span
          if (span.start > lastIndex) {
            parts.push(
              <span key={`${paraIndex}-text-${lastIndex}`}>
                {displayText.slice(lastIndex, span.start)}
              </span>,
            );
          }

          // Add highlighted text
          if (span.end > span.start) {
            parts.push(
              <mark
                key={`${paraIndex}-mark-${spanIdx}`}
                style={{ backgroundColor: "#FFF89A" }}
              >
                {displayText.slice(span.start, span.end)}
              </mark>,
            );
          }

          lastIndex = span.end;
        });

      // Add remaining unhighlighted text
      if (lastIndex < displayText.length) {
        parts.push(
          <span key={`${paraIndex}-text-end`}>
            {displayText.slice(lastIndex)}
          </span>,
        );
      }

      processedNodes.push(
        <Typography key={paraIndex} sx={sx}>
          {parts}
        </Typography>,
      );
    } else {
      // No highlights - render plain text
      processedNodes.push(
        <Typography key={paraIndex} sx={sx}>
          {hasListNumberWithSummary && listNumber && (
            <strong>{listNumber} </strong>
          )}
          {displayText}
        </Typography>,
      );
    }
  });

  return <Box>{processedNodes}</Box>;
};
